import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowUp,
  faArrowDown,
  faAngleDoubleUp,
  faMinus,
  faForward,
  faPause,
  faPlay,
  faVolumeMute,
  faVolumeUp,
} from '@fortawesome/free-solid-svg-icons';
import ScaleLoader from 'react-spinners/ScaleLoader';

import bannerLogo from '../images/Jook_Web_White.png';

import JukeboxVideoRow, { JukeboxRowAction } from './JukeboxVideoRow';
import VenuePayouts, { PayoutStatus } from './VenuePayouts';
import PlayerAdmins, { PlayerAdminEntry } from './PlayerAdmins';
import NumberStepper from './NumberStepper';
import TriviaHost from './TriviaHost';
import VenueMapSettings from './VenueMapSettings';
import ShoutoutReview, { ShoutoutAction, ShoutoutModeSetting, useShoutoutReview } from './ShoutoutReview';
import { ShoutoutBan } from '../utils/Shoutouts';
import ConfirmModal from './ConfirmModal';
import defaultValues from '../config/defaultValues.json';

import * as JKYModel from '../custom-types';
import api from '../utils/Api';
import usePlayerUpdateHandler from '../utils/custom-hooks/PlayerUpdateHandler';
import { authContext } from '../contexts/AuthContext';
import { isFillerVideo } from '../utils/Filler';
import { ACCEPTED_LOGO_TYPES, MIN_LOGO_HEIGHT, MIN_LOGO_WIDTH } from '../utils/VenueLogo';
import { useVenueLogoEditor } from '../utils/custom-hooks/VenueLogoEditor';

import './JukeboxView.scss';
import './ControlPanelView.scss';
import AppHeader from './AppHeader';
import FooterContent from './FooterContent';
import { trackEvent } from '../utils/Analytics';

interface ControlPanelViewProps {
  playerIdentifier: string;
}

type Tab = 'queue' | 'trivia' | 'shoutouts' | 'settings' | 'earnings';
type Team = { role: 'owner' | 'admin' | null; manage?: boolean; admins?: PlayerAdminEntry[] };
type Notice = { tone: 'ok' | 'bad'; text: string };
type Move = 'up' | 'down' | 'next';

const LENGTH_CHOICES = [
  { seconds: 0, label: 'No limit' },
  { seconds: 240, label: '4 minutes' },
  { seconds: 300, label: '5 minutes' },
  { seconds: 360, label: '6 minutes' },
  { seconds: 480, label: '8 minutes' },
  { seconds: 600, label: '10 minutes' },
  { seconds: 900, label: '15 minutes' },
];

const isPaid = (video: JKYModel.QueueVideo) => !!(video.isBump || video.isSuperBump);

// Each tap on Vol − or Vol + moves the TV this much, as in the phone app.
const VOLUME_STEP = 0.1;

interface RuleSwitchProps {
  label: string;
  checked: boolean;
  disabled: boolean;
  onChange(checked: boolean): void;
}

/** An on/off switch big enough for a thumb, labelled with what it turns on. */
const RuleSwitch = ({ label, checked, disabled, onChange }: RuleSwitchProps) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    className={checked ? 'control-switch is-on' : 'control-switch'}
    disabled={disabled}
    onClick={() => onChange(!checked)}
  >
    <span className="control-switch-track" aria-hidden="true">
      <span className="control-switch-knob" />
    </span>
    <span className="control-rule-label">{label}</span>
    <span className="control-switch-state">{checked ? 'On' : 'Off'}</span>
  </button>
);

interface NameTickerProps {
  player: JKYModel.Player;
  disabled: boolean;
  onSave(change: Partial<JKYModel.Player>): void;
}

/** The jukebox's name and the three ticker lines, saved together. */
const NameTickerSettings = ({ player, disabled, onSave }: NameTickerProps) => {
  const [name, setName] = useState(player.name || '');
  const [lines, setLines] = useState([player.tickerLineOne || '', player.tickerLineTwo || '', player.tickerLineThree || '']);
  const changed =
    name !== (player.name || '') ||
    lines[0] !== (player.tickerLineOne || '') ||
    lines[1] !== (player.tickerLineTwo || '') ||
    lines[2] !== (player.tickerLineThree || '');
  const setLine = (index: number, value: string) => setLines(current => current.map((line, i) => (i === index ? value : line)));

  return (
    <form
      className="control-rule control-name-ticker"
      onSubmit={e => {
        e.preventDefault();
        onSave({ name: name.trim(), tickerLineOne: lines[0], tickerLineTwo: lines[1], tickerLineThree: lines[2] });
      }}
    >
      <label className="control-rule-label" htmlFor="control-name">
        Jukebox name
      </label>
      <input
        id="control-name"
        className="control-text"
        type="text"
        maxLength={defaultValues.player.nameCharLimit}
        value={name}
        onChange={e => setName(e.target.value)}
      />
      <div className="control-rule-label control-ticker-label">Ticker on the TV</div>
      {lines.map((line, index) => (
        <input
          key={index}
          className="control-text"
          type="text"
          aria-label={`Ticker line ${index + 1}`}
          placeholder={`Line ${index + 1}`}
          maxLength={defaultValues.player.tickerCharLimit}
          value={line}
          onChange={e => setLine(index, e.target.value)}
        />
      ))}
      <button type="submit" className="control-logo-upload" disabled={disabled || !changed || !name.trim()}>
        Save name and ticker
      </button>
    </form>
  );
};

/**
 * The venue owner's control panel, on a phone behind the bar: skip what is playing, pause,
 * mute or turn the TV up and down, move songs about, and set the house rules for what
 * patrons may queue.
 *
 * The owner's admins (the manager on duty, say) get the queue half of it: skip, pause,
 * volume, move, remove. The Settings and Earnings tabs are the owner's alone, and the server
 * refuses those changes from anyone else.
 *
 * It reads and writes the same player the TV screen and the jukebox screen do, and gets
 * their live updates, so a change here shows on the wall straight away.
 *
 * Paid songs keep their place. Nothing here can push a bumped or super bumped song further
 * from the front; the server refuses it too.
 */
const ControlPanelView = (props: ControlPanelViewProps) => {
  const { playerIdentifier } = props;
  const context = useContext(authContext);
  const token = context.auth.token;
  const currentUser = context.currentUser;

  const { player: livePlayer, error: liveError, trivia, refreshTrivia } = usePlayerUpdateHandler(playerIdentifier);
  const triviaOn = !!(trivia && trivia.active);
  const [confirmJukebox, setConfirmJukebox] = useState(false);
  const [player, setPlayer] = useState<JKYModel.Player>();
  const [actionError, setActionError] = useState<string>(null);
  const loadError = actionError || (liveError && liveError.message);

  const [tab, setTab] = useState<Tab>('queue');
  const [busyKey, setBusyKey] = useState<string>(null);
  const [confirmSkip, setConfirmSkip] = useState(false);
  const [notice, setNotice] = useState<Notice>(null);
  const noticeTimer = useRef<number>(null);
  const confirmTimer = useRef<number>(null);
  const [payouts, setPayouts] = useState<PayoutStatus>(null);
  const logoInput = useRef<HTMLInputElement>(null);
  // The live update brings the new logo too, but this shows it without waiting for it.
  const logoEditor = useVenueLogoEditor(player, logoUrl => setPlayer(current => current && { ...current, logoUrl }));
  const [team, setTeam] = useState<Team>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (livePlayer) {
      setPlayer(livePlayer);
    }
  }, [livePlayer]);

  useEffect(
    () => () => {
      window.clearTimeout(noticeTimer.current);
      window.clearTimeout(confirmTimer.current);
    },
    [],
  );

  const refreshPlayer = () =>
    api
      .players(`identifier/${playerIdentifier}`, 'GET', token, null)
      .then(response => {
        setPlayer(response);
        setActionError(null);
      })
      .catch(error => setActionError(error.message));

  const showNotice = (tone: 'ok' | 'bad', text: string) => {
    window.clearTimeout(noticeTimer.current);
    setNotice({ tone, text });
    noticeTimer.current = window.setTimeout(() => setNotice(null), 5000);
  };

  // `event` names the Google Analytics event sent once the change went through.
  const run = async (key: string, work: () => Promise<any>, done: string, event?: [string, Record<string, any>?]) => {
    setBusyKey(key);
    try {
      await work();
      if (event) trackEvent(event[0], { player_code: playerIdentifier, ...(event[1] || {}) });
      await refreshPlayer();
      showNotice('ok', done);
    } catch (error) {
      showNotice('bad', error.message);
    } finally {
      setBusyKey(null);
    }
  };

  const queueVideos: JKYModel.QueueVideo[] = (player && player.queue && player.queue.videos) || [];
  // The filler is not a song, so it is shown as an empty queue rather than as Sky News.
  const nowPlaying = player && !isFillerVideo(player.nowPlayingVideo) ? player.nowPlayingVideo : null;
  const isOwner = !!(player && player.user && currentUser && player.user.id === currentUser.id);
  const isAdmin = !isOwner && !!team && team.role === 'admin';
  const canControl = isOwner || isAdmin;
  // The owner's settings: the owner, or a site admin stepping in. Earnings stay the owner's.
  const canManage = isOwner || (!!team && !!team.manage);
  const canSkip = !!nowPlaying;
  const playerId = player && player.id;

  // Who is on the team. For the owner it lists the admins; for anyone else it only says
  // whether they are one.
  const refreshTeam = () =>
    api
      .players(`${playerId}/team`, 'GET', token, null)
      .then(response => setTeam(response))
      .catch(() => setTeam({ role: null }));

  useEffect(() => {
    if (playerId) {
      refreshTeam();
    }
  }, [playerId]);

  // Paid shout-outs. The tab is there while they are on, or while some still wait for review,
  // show on the TV or are banned, so they can be dealt with even with shout-outs off.
  const shoutoutMode = (player && player.shoutoutMode) || 'off';
  const shoutouts = useShoutoutReview(playerIdentifier, token, canControl, shoutoutMode);
  const showShoutoutTab =
    canControl &&
    (shoutoutMode !== 'off' || shoutouts.pending.length > 0 || shoutouts.live.length > 0 || shoutouts.bans.length > 0);
  const decideShoutout = async (shoutout, action: ShoutoutAction) => {
    if (action === 'ban') {
      const { problem, response } = await shoutouts.ban(shoutout);
      if (problem) {
        showNotice('bad', problem);
        return;
      }
      trackEvent('shoutout_reviewed', { player_code: playerIdentifier, action: 'ban' });
      const result = (response && response.ban) || {};
      const who = shoutout.username || 'They';
      const parts = [`${who} can't buy shout-outs at this jukebox now.`];
      if (result.refunded > 0) parts.push(`${result.refunded === 1 ? 'One waiting shout-out was' : `${result.refunded} waiting shout-outs were`} refunded.`);
      if (result.refundFailed > 0) parts.push('A refund did not go through, so it is still waiting. Try Reject again shortly.');
      showNotice(result.refundFailed > 0 ? 'bad' : 'ok', parts.join(' '));
      return;
    }
    const problem = await shoutouts.decide(shoutout, action);
    if (!problem) trackEvent('shoutout_reviewed', { player_code: playerIdentifier, action });
    if (problem) showNotice('bad', problem);
    else
      showNotice(
        'ok',
        action === 'approve' ? 'On the TV now.' : action === 'remove' ? 'Deleted. It is off the TV.' : 'Rejected and refunded.',
      );
  };
  const unbanShoutouts = async (ban: ShoutoutBan) => {
    const problem = await shoutouts.unban(ban.userId);
    if (problem) showNotice('bad', problem);
    else showNotice('ok', `Unbanned ${ban.username || 'that account'} from this ticker.`);
  };

  const inviteAdmin = async (email: string) => {
    setBusyKey('admins');
    try {
      const response = await api.players(`${playerId}/admins`, 'POST', token, { email });
      setTeam(current => ({ ...current, admins: response.admins }));
      trackEvent('player_admin_invited', { player_code: playerIdentifier });
      if (response.added) {
        showNotice('ok', `${email} is now an admin.${response.emailed ? ' We emailed them to say so.' : ''}`);
      } else if (response.emailed) {
        showNotice('ok', `Invite emailed to ${email}.`);
      } else {
        showNotice('ok', `Invited. Copy the link below and send it to ${email}.`);
      }
      return true;
    } catch (error) {
      showNotice('bad', error.message);
      return false;
    } finally {
      setBusyKey(null);
    }
  };

  const removeAdmin = async (admin: PlayerAdminEntry) => {
    setBusyKey('admins');
    try {
      const response = await api.players(`${playerId}/admins/${admin.id}`, 'DELETE', token, null);
      setTeam(current => ({ ...current, admins: response.admins }));
      trackEvent('player_admin_removed', { player_code: playerIdentifier });
      showNotice('ok', admin.pending ? 'Invite cancelled.' : `${admin.username || admin.email} is no longer an admin.`);
    } catch (error) {
      showNotice('bad', error.message);
    } finally {
      setBusyKey(null);
    }
  };

  // The owner's share of bump sales. Read once they are known to be the owner, and again
  // when Stripe sends them back here from signing up.
  const refreshPayouts = () =>
    api
      .users('payouts', 'GET', token, null)
      .then(response => setPayouts(response))
      .catch(() => setPayouts(null));

  useEffect(() => {
    if (isOwner) {
      refreshPayouts();
    }
  }, [isOwner]);

  // Back from Stripe's sign-up pages. The flag comes off the address so a reload does not
  // say it again.
  useEffect(() => {
    const outcome = new URLSearchParams(location.search).get('payouts');
    if (outcome !== 'done' && outcome !== 'expired') {
      return;
    }
    navigate(location.pathname, { replace: true });
    setTab('earnings');
    if (outcome === 'expired') {
      showNotice('bad', 'That sign-up link had expired. Tap the button to carry on.');
      return;
    }
    refreshPayouts();
    trackEvent('payouts_setup_returned', { player_code: playerIdentifier });
    showNotice('ok', 'Back from Stripe.');
  }, [location.search]);

  const openStripe = (path: string, body: any) =>
    run('payouts', async () => {
      const { url } = await api.users(path, 'POST', token, body);
      trackEvent(path === 'payouts/setup' ? 'payouts_setup_started' : 'payouts_dashboard_opened', {
        player_code: playerIdentifier,
      });
      window.location.assign(url);
    }, 'Opening Stripe.');

  const setupPayouts = () => openStripe('payouts/setup', { returnPath: location.pathname });
  const payoutDashboard = () => openStripe('payouts/dashboard', {});

  // The TV between the jukebox and trivia night. Switching to trivia opens a new game's
  // lobby with the saved setup. Switching back mid-game ends it, so that takes two taps.
  const switchMode = async (mode: 'jukebox' | 'trivia') => {
    if (mode === 'trivia' && !triviaOn) {
      setTab('trivia');
      await run(
        'mode',
        () => api.trivia(`${playerIdentifier}/start`, 'POST', token, {}),
        'Trivia is on the TV. Players can join now.',
        ['trivia_started'],
      );
      refreshTrivia();
      return;
    }
    if (mode === 'jukebox' && triviaOn) {
      const midGame = trivia.phase !== 'lobby' && trivia.phase !== 'final';
      if (midGame && !confirmJukebox) {
        setConfirmJukebox(true);
        window.clearTimeout(confirmTimer.current);
        confirmTimer.current = window.setTimeout(() => setConfirmJukebox(false), 4000);
        return;
      }
      setConfirmJukebox(false);
      await run('mode', () => api.trivia(`${playerIdentifier}/stop`, 'POST', token, {}), 'Back to the jukebox.', [
        'trivia_stopped',
        { mid_game: midGame },
      ]);
      refreshTrivia();
    }
  };

  // Two taps, because a skip cannot be undone and the song may be one somebody paid for.
  const skip = () => {
    if (!confirmSkip) {
      setConfirmSkip(true);
      window.clearTimeout(confirmTimer.current);
      confirmTimer.current = window.setTimeout(() => setConfirmSkip(false), 4000);
      return;
    }
    window.clearTimeout(confirmTimer.current);
    setConfirmSkip(false);
    run(
      'skip',
      () => api.players('removeVideo', 'POST', token, { playerId: player.id, videoId: nowPlaying.id }),
      'Skipped.',
      ['song_skipped', { video_id: nowPlaying.thirdPartyId }],
    );
  };

  const move = (video: JKYModel.QueueVideo, to: Move) =>
    run(
      `move:${video.id}`,
      () => api.players('moveVideo', 'POST', token, { playerId: player.id, videoId: video.id, to }),
      to === 'next' ? 'Moved up as far as it can go.' : to === 'up' ? 'Moved up.' : 'Moved down.',
      ['song_moved', { direction: to }],
    );

  const remove = (video: JKYModel.QueueVideo) =>
    run(
      `move:${video.id}`,
      () => api.players('removeVideo', 'POST', token, { playerId: player.id, videoId: video.id }),
      'Removed from the queue.',
      ['song_removed', { video_id: video.thirdPartyId, by: 'control_panel' }],
    );

  const saveRule = (key: string, change: Partial<JKYModel.Player>) =>
    run(key, () => api.players(`${player.id}`, 'PATCH', token, change), 'Saved.', [
      'jukebox_setting_changed',
      { setting: Object.keys(change).join(',') },
    ]);

  // The TV's sound and playback, as in the phone app. The owner's admins may change these too.
  const savePlayback = (key: string, change: Partial<JKYModel.Player>, done: string) =>
    run(key, () => api.players(`${player.id}`, 'PATCH', token, change), done, [
      'jukebox_setting_changed',
      { setting: Object.keys(change).join(',') },
    ]);
  const paused = !!player && player.isPlaying === false;
  const volume =
    player && player.volumeLevel !== null && Number.isFinite(Number(player.volumeLevel)) ? Number(player.volumeLevel) : 0.5;
  const setVolume = (next: number) => {
    const level = Math.round(Math.min(1, Math.max(0, next)) * 10) / 10;
    savePlayback('volume', { volumeLevel: level }, `Volume ${Math.round(level * 100)}%.`);
  };

  // Unpauses, unmutes and sets the volume to half, and repairs the queue if it can.
  const resetJukebox = () => {
    setConfirmReset(false);
    run('reset', () => api.players(`reset/${player.id}`, 'POST', token, null), 'Reset.', ['jukebox_reset']);
  };

  const deleteJukebox = async () => {
    setConfirmDelete(false);
    setBusyKey('delete');
    try {
      await api.players(`${player.id}`, 'DELETE', token, null);
      trackEvent('jukebox_deleted', { player_code: playerIdentifier });
      navigate('/home');
    } catch (error) {
      showNotice('bad', error.message);
      setBusyKey(null);
    }
  };

  // The same rules the server applies, so a button that would only be refused is disabled.
  const queueActions = (video: JKYModel.QueueVideo, index: number): JukeboxRowAction[] => {
    const busy = !!busyKey;
    const above = queueVideos[index - 1];
    let nextSlot = index;
    while (nextSlot > 0 && !isPaid(queueVideos[nextSlot - 1])) {
      nextSlot--;
    }
    return [
      {
        key: 'up',
        label: 'Up',
        icon: faArrowUp,
        disabled: busy || !above || isPaid(above),
        onClick: () => move(video, 'up'),
      },
      {
        key: 'down',
        label: 'Down',
        icon: faArrowDown,
        disabled: busy || index === queueVideos.length - 1 || isPaid(video),
        onClick: () => move(video, 'down'),
      },
      {
        key: 'next',
        label: 'Play next',
        icon: faAngleDoubleUp,
        disabled: busy || nextSlot === index,
        onClick: () => move(video, 'next'),
      },
      {
        key: 'remove',
        label: 'Remove',
        icon: faMinus,
        disabled: busy,
        onClick: () => remove(video),
      },
    ];
  };

  // A non-owner waits for the team answer before being told the panel is not theirs.
  if ((!player && !loadError) || (!!player && !isOwner && !team)) {
    return (
      <div className="jukebox-loading">
        <h1>Opening the control panel</h1>
        <ScaleLoader cssOverride={{ display: 'flex' }} height={35} width={4} radius={2} color={'#fff'} loading={true} />
      </div>
    );
  }

  const maxSongSeconds = (player && Number(player.maxSongSeconds)) || 0;
  const lengthChoices = LENGTH_CHOICES.some(choice => choice.seconds === maxSongSeconds)
    ? LENGTH_CHOICES
    : [...LENGTH_CHOICES, { seconds: maxSongSeconds, label: `${Math.round(maxSongSeconds / 60)} minutes` }];
  const paidCount = queueVideos.filter(isPaid).length;

  return (
    <div className="jukebox">
      <AppHeader />

      <div className="jukebox-body">
        {!!loadError && !player && (
          <div className="jukebox-panel">
            <p className="jukebox-empty">We could not find a jukebox with the code {playerIdentifier.toUpperCase()}.</p>
            <Link className="jukebox-primary-link" to="/home">
              Back to home
            </Link>
          </div>
        )}

        {!!player && !canControl && (
          <div className="jukebox-panel">
            <p className="jukebox-empty">Only the owner of {player.name} and the admins they invite can use its control panel.</p>
            <Link className="jukebox-primary-link" to={`/jukebox/${playerIdentifier}`}>
              Add songs instead
            </Link>
          </div>
        )}

        {!!player && canControl && (
          <div className="jukebox-panel">
            <div className="jukebox-player">
              <div className="jukebox-player-name">{player.name}</div>
              <div className="jukebox-player-code">{playerIdentifier.toUpperCase()}</div>
            </div>
            <div className="control-subtitle">{isOwner ? 'Control panel' : canManage ? 'Control panel · Site admin' : 'Control panel · Admin'}</div>
            <div className="control-links">
              <Link className="jukebox-primary-link" to={`/jukebox/${playerIdentifier}`}>
                Add songs
              </Link>
              <Link className="jukebox-primary-link" to={`/player/${playerIdentifier}`}>
                Open TV screen
              </Link>
            </div>

            <div className="mode-switch-caption">The TV is showing</div>
            <div className="mode-switch" role="radiogroup" aria-label="What the TV shows">
              <button
                type="button"
                role="radio"
                aria-checked={!triviaOn}
                className={[
                  'mode-switch-option',
                  !triviaOn ? 'is-active' : '',
                  confirmJukebox ? 'is-confirming' : '',
                ].join(' ')}
                disabled={!!busyKey}
                onClick={() => switchMode('jukebox')}
              >
                {confirmJukebox ? 'Tap again to end the game' : 'Jukebox'}
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={triviaOn}
                className={triviaOn ? 'mode-switch-option is-active' : 'mode-switch-option'}
                disabled={!!busyKey}
                onClick={() => switchMode('trivia')}
              >
                Trivia
              </button>
            </div>

            <ul className="jukebox-list">
              {!!nowPlaying ? (
                <JukeboxVideoRow
                  video={nowPlaying}
                  eyebrow="Now playing"
                  isBump={nowPlaying.isBump}
                  isSuperBump={nowPlaying.isSuperBump}
                  actions={[
                    {
                      key: 'skip',
                      label: confirmSkip ? 'Tap again to skip' : 'Skip song',
                      icon: faForward,
                      active: confirmSkip,
                      disabled: !!busyKey || !canSkip,
                      onClick: skip,
                    },
                  ]}
                />
              ) : (
                <li className="jukebox-row">
                  <div className="jukebox-row-eyebrow">Now playing</div>
                  <div className="jukebox-row-title">{queueVideos.length === 0 ? 'Empty Queue' : 'Nothing'}</div>
                </li>
              )}
            </ul>

            <div className="control-playback">
              <button
                type="button"
                className="control-playback-button"
                disabled={!!busyKey}
                onClick={() => savePlayback('playing', { isPlaying: paused }, paused ? 'Playing.' : 'Paused.')}
              >
                <FontAwesomeIcon icon={paused ? faPlay : faPause} /> {paused ? 'Play' : 'Pause'}
              </button>
              <button
                type="button"
                className="control-playback-button"
                disabled={!!busyKey}
                onClick={() => savePlayback('muted', { isMuted: !player.isMuted }, player.isMuted ? 'Sound on.' : 'Muted.')}
              >
                <FontAwesomeIcon icon={player.isMuted ? faVolumeUp : faVolumeMute} /> {player.isMuted ? 'Unmute' : 'Mute'}
              </button>
              <div className="control-volume" role="group" aria-label="Volume">
                <button
                  type="button"
                  className="control-playback-button"
                  aria-label="Volume down"
                  disabled={!!busyKey || volume <= 0}
                  onClick={() => setVolume(volume - VOLUME_STEP)}
                >
                  Vol −
                </button>
                <span className="control-volume-level">{Math.round(volume * 100)}%</span>
                <button
                  type="button"
                  className="control-playback-button"
                  aria-label="Volume up"
                  disabled={!!busyKey || volume >= 1}
                  onClick={() => setVolume(volume + VOLUME_STEP)}
                >
                  Vol +
                </button>
              </div>
            </div>

            <div className="jukebox-tabs control-tabs" role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={tab === 'queue'}
                className={tab === 'queue' ? 'jukebox-tab is-active' : 'jukebox-tab'}
                onClick={() => setTab('queue')}
              >
                Queue ({queueVideos.length})
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={tab === 'trivia'}
                className={tab === 'trivia' ? 'jukebox-tab is-active' : 'jukebox-tab'}
                onClick={() => setTab('trivia')}
              >
                Trivia
              </button>
              {showShoutoutTab && (
                <button
                  type="button"
                  role="tab"
                  aria-selected={tab === 'shoutouts'}
                  className={tab === 'shoutouts' ? 'jukebox-tab is-active' : 'jukebox-tab'}
                  onClick={() => setTab('shoutouts')}
                >
                  Shout-outs{shoutouts.pending.length > 0 ? ` (${shoutouts.pending.length})` : ''}
                </button>
              )}
              {canManage && (
                <button
                  type="button"
                  role="tab"
                  aria-selected={tab === 'settings'}
                  className={tab === 'settings' ? 'jukebox-tab is-active' : 'jukebox-tab'}
                  onClick={() => setTab('settings')}
                >
                  Settings
                </button>
              )}
              {isOwner && !!payouts && payouts.available && (
                <button
                  type="button"
                  role="tab"
                  aria-selected={tab === 'earnings'}
                  className={tab === 'earnings' ? 'jukebox-tab is-active' : 'jukebox-tab'}
                  onClick={() => setTab('earnings')}
                >
                  Earnings
                </button>
              )}
            </div>

            {!!notice && <div className={`jukebox-notice jukebox-notice-${notice.tone}`}>{notice.text}</div>}

            {tab === 'shoutouts' && showShoutoutTab && (
              <ShoutoutReview
                mode={shoutoutMode}
                minutes={shoutouts.minutes}
                pending={shoutouts.pending}
                live={shoutouts.live}
                recent={shoutouts.recent}
                bans={shoutouts.bans}
                busyId={shoutouts.busyId}
                onDecide={decideShoutout}
                onUnban={unbanShoutouts}
              />
            )}

            {tab === 'queue' && (
              <div>
                {queueVideos.length === 0 && <p className="jukebox-empty">Nothing is waiting to play.</p>}
                {paidCount > 0 && (
                  <p className="control-hint">
                    Songs with a gold or silver border were bumped by a patron. They keep their place: you can move
                    them up but not down, and nothing can move above them.
                  </p>
                )}
                <ul className="jukebox-list">
                  {queueVideos.map((video, index) => (
                    <JukeboxVideoRow
                      key={video.id}
                      video={video}
                      eyebrow={index === 0 ? 'Up next' : undefined}
                      isBump={video.isBump}
                      isSuperBump={video.isSuperBump}
                      actions={queueActions(video, index)}
                    />
                  ))}
                </ul>
              </div>
            )}

            {tab === 'trivia' && (
              <TriviaHost
                playerIdentifier={playerIdentifier}
                token={token}
                trivia={trivia}
                onNotice={showNotice}
              />
            )}

            {tab === 'settings' && canManage && (
              <div className="control-rules">
                <h3 className="control-section-title">Name and ticker</h3>
                <NameTickerSettings
                  key={`${player.id}:${player.name}:${player.tickerLineOne}:${player.tickerLineTwo}:${player.tickerLineThree}`}
                  player={player}
                  disabled={!!busyKey}
                  onSave={change => saveRule('nameTicker', change)}
                />

                <h3 className="control-section-title">Song rules</h3>
                <p className="control-hint">
                  These apply to songs patrons add. Songs you and your admins add are not limited.
                </p>

                <div className="control-rule">
                  <div className="control-rule-label">Songs per person</div>
                  <div className="control-stepper">
                    <NumberStepper
                      aria-label="Songs per person"
                      value={player.queueAddLimit != null ? Number(player.queueAddLimit) : 3}
                      min={1}
                      max={20}
                      disabled={!!busyKey}
                      onKeyPress={e => e.preventDefault()}
                      onChange={value => value != null && saveRule('queueAddLimit', { queueAddLimit: value })}
                    />
                  </div>
                  <div className="control-rule-help">How many songs one patron can have waiting at a time.</div>
                </div>

                <div className="control-rule">
                  <label className="control-rule-label" htmlFor="control-max-length">
                    Longest song
                  </label>
                  <select
                    id="control-max-length"
                    className="control-select"
                    value={maxSongSeconds}
                    disabled={!!busyKey}
                    onChange={e => saveRule('maxSongSeconds', { maxSongSeconds: Number(e.target.value) })}
                  >
                    {lengthChoices.map(choice => (
                      <option key={choice.seconds} value={choice.seconds}>
                        {choice.label}
                      </option>
                    ))}
                  </select>
                  <div className="control-rule-help">Longer songs, and live streams, are refused.</div>
                </div>

                <div className="control-rule">
                  <RuleSwitch
                    label="Block explicit songs"
                    checked={!!player.blockExplicit}
                    disabled={!!busyKey}
                    onChange={checked => saveRule('blockExplicit', { blockExplicit: checked })}
                  />
                  <div className="control-rule-help">
                    Refuses songs YouTube age-restricts or that say Explicit, Uncensored or Dirty in the title, and
                    turns on YouTube's strict SafeSearch for patrons. YouTube has no exact explicit flag, so an
                    unlabelled song can still slip through. Skip it if one does.
                  </div>
                </div>

                <h3 className="control-section-title">On the TV</h3>
                <div className="control-rule">
                  <RuleSwitch
                    label="Closed captions on the TV"
                    checked={player.showCaptions !== false}
                    disabled={!!busyKey}
                    onChange={checked => saveRule('showCaptions', { showCaptions: checked })}
                  />
                  <div className="control-rule-help">Shown when the video has them.</div>
                </div>

                <div className="control-rule">
                  <RuleSwitch
                    label="Song leaderboard"
                    checked={!!player.showLeaderboard}
                    disabled={!!busyKey}
                    onChange={checked => saveRule('showLeaderboard', { showLeaderboard: checked })}
                  />
                  <div className="control-rule-help">
                    Shows the five people who played the most songs here in the last 7 days, by username, on the
                    jukebox page and the TV.
                  </div>
                </div>

                <div className="control-rule">
                  <div className="control-rule-label">Venue logo</div>
                  <div className="control-rule-help">
                    Shown on the TV while nothing is queued. Without one, the screen shows the Jookable logo.
                  </div>
                  <div className="control-logo-preview">
                    <img src={player.logoUrl || bannerLogo} alt={player.logoUrl ? 'Your venue logo' : 'Jookable logo'} />
                  </div>
                  <input
                    ref={logoInput}
                    type="file"
                    accept={ACCEPTED_LOGO_TYPES.join(',')}
                    hidden
                    onChange={e => {
                      const file = e.target.files && e.target.files[0];
                      // Cleared so picking the same file again still fires a change.
                      e.target.value = '';
                      if (file) logoEditor.upload(file);
                    }}
                  />
                  <div className="control-logo-actions">
                    <button
                      type="button"
                      className="control-logo-upload"
                      disabled={logoEditor.busy}
                      onClick={() => logoInput.current && logoInput.current.click()}
                    >
                      {logoEditor.busy ? 'Saving…' : player.logoUrl ? 'Change logo' : 'Upload logo'}
                    </button>
                    {!!player.logoUrl && (
                      <button
                        type="button"
                        className="jukebox-action"
                        disabled={logoEditor.busy}
                        onClick={logoEditor.remove}
                      >
                        Use Jookable logo
                      </button>
                    )}
                  </div>
                  {logoEditor.message && (
                    <div className={`jukebox-notice jukebox-notice-${logoEditor.tone}`}>{logoEditor.message}</div>
                  )}
                  <div className="control-rule-help">
                    {`A PNG or JPG at least ${MIN_LOGO_WIDTH} pixels wide or ${MIN_LOGO_HEIGHT} tall, up to 20 MB. ` +
                      'A PNG with a see-through background looks best. Anything bigger than 4K is shrunk to fit.'}
                  </div>
                </div>

                <h3 className="control-section-title">Paid shout-outs</h3>
                <ShoutoutModeSetting
                  mode={shoutoutMode}
                  minutes={shoutouts.minutes}
                  disabled={!!busyKey}
                  onChange={mode => saveRule('shoutoutMode', { shoutoutMode: mode })}
                />
                {shoutoutMode !== 'off' && (
                  <p className="control-hint">Shout-outs waiting for you, and the ones on the TV, are in the Shout-outs tab.</p>
                )}

                <h3 className="control-section-title">Venues map</h3>
                <VenueMapSettings playerId={player.id} playerName={player.name} token={token} onNotice={showNotice} />

                <h3 className="control-section-title">Admins</h3>
                <PlayerAdmins
                  admins={(team && team.admins) || []}
                  busy={!!busyKey}
                  onInvite={inviteAdmin}
                  onRemove={removeAdmin}
                  onCopied={text => showNotice('ok', text)}
                />

                <h3 className="control-section-title">Reset or delete</h3>
                <div className="control-rule">
                  <div className="control-danger-actions">
                    <button type="button" className="jukebox-action" disabled={!!busyKey} onClick={() => setConfirmReset(true)}>
                      Reset jukebox
                    </button>
                    <button
                      type="button"
                      className="jukebox-action control-delete"
                      disabled={!!busyKey}
                      onClick={() => setConfirmDelete(true)}
                    >
                      Delete jukebox
                    </button>
                  </div>
                  <div className="control-rule-help">
                    Reset unpauses and unmutes the TV, sets the volume to half and repairs the queue. Delete removes
                    this jukebox for good.
                  </div>
                </div>
                {confirmReset && (
                  <ConfirmModal
                    isOpen={confirmReset}
                    prompt={`Reset ${player.name}? The TV unpauses, unmutes and goes to half volume. If the queue is damaged and cannot be repaired, it is cleared.`}
                    confirmTitle="Reset"
                    onCancelClick={() => setConfirmReset(false)}
                    onConfirmClick={resetJukebox}
                  />
                )}
                {confirmDelete && (
                  <ConfirmModal
                    isOpen={confirmDelete}
                    prompt={`Delete ${player.name} (${playerIdentifier.toUpperCase()}) for good?`}
                    confirmTitle="Delete"
                    onCancelClick={() => setConfirmDelete(false)}
                    onConfirmClick={deleteJukebox}
                  />
                )}
              </div>
            )}

            {tab === 'earnings' && isOwner && !!payouts && payouts.available && (
              <VenuePayouts
                token={token}
                playerId={player.id}
                status={payouts}
                busy={!!busyKey}
                onSetup={setupPayouts}
                onDashboard={payoutDashboard}
              />
            )}
          </div>
        )}
      </div>

      <div className="footer">
        <FooterContent />
      </div>
    </div>
  );
};

export default ControlPanelView;
